import TabStudents from './components/TabStudents';
import { CourseFeaturesPatch, TabFeatures } from './components/TabFeatures';
import { CoursesIconComponent } from 'assets/icons/courses';
import { EditLayout } from 'components/layout/layout/EditLayout';
import { Flex, Tabs, Tag } from 'antd';
import { ICourseShow } from 'interfaces/courses';
import { PageHeader } from 'components/professor_area/PageHeader';
import { TabAcademicCalendar } from 'components/courses/TabAcademicCalendar';
import { TabAssets } from './components/TabAssets';
import { TabDisciplines } from './components/TabDisciplines';
import { TabInfo } from './components/TabInfo';
import { TabInstitutionalMaterials } from 'components/courses/TabInstitutionalMaterials';
import { isFeatureEnabled } from 'services/tenants';
import { useCallback, useEffect, useState } from 'react';
import { useCan } from '@refinedev/core';
import { useForm } from '@refinedev/antd';

export const CourseEdit = () => {
  const [course, setCourse] = useState<ICourseShow>();
  const [activeTab, setActiveTab] = useState('info');
  const { data: canShow } = useCan({ resource: 'courses', action: 'show' });
  const { data: canEdit } = useCan({ resource: 'courses', action: 'edit' });
  const { data: canListDisciplines } = useCan({
    resource: 'graduate_disciplines',
    action: 'list'
  });
  const { data: canListAcademicCalendars } = useCan({
    resource: 'courses/academic_calendars',
    action: 'list'
  });
  const { data: canCreateAcademicCalendars } = useCan({
    resource: 'courses/academic_calendars',
    action: 'create'
  });
  const { data: canEditAcademicCalendars } = useCan({
    resource: 'courses/academic_calendars',
    action: 'edit'
  });
  const { data: canDeleteAcademicCalendars } = useCan({
    resource: 'courses/academic_calendars',
    action: 'delete'
  });

  const { formProps, saveButtonProps, queryResult } = useForm<ICourseShow>({
    redirect: 'edit'
  });

  useEffect(() => {
    setCourse(queryResult?.data?.data);
  }, [queryResult]);

  const onFinish = useCallback(
    (data: ICourseShow | CourseFeaturesPatch) => {
      formProps.onFinish!({ ...data });
    },
    [formProps.onFinish]
  );

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  const canSeeDisciplinesTab = Boolean(canListDisciplines?.can);
  const canSeeAcademicCalendarTab =
    isFeatureEnabled('course_academic_calendar') &&
    course?.with_disciplines &&
    Boolean(canListAcademicCalendars?.can);

  return (
    <EditLayout
      title="Editar curso avulso"
      saveButtonProps={
        canEdit?.can &&
        !['features', 'academic_calendar', 'institutional_materials'].includes(
          activeTab
        )
          ? saveButtonProps
          : { hidden: true }
      }
      isLoading={queryResult?.isLoading}
      isFetched={queryResult?.isFetched}
      record={queryResult?.data?.data}
      footerButtonProps={{
        style: {
          float: 'right'
        }
      }}
    >
      <PageHeader
        title={course?.name}
        icon={CoursesIconComponent}
        meta={
          <Flex>
            <Tag>Curso avulso</Tag>
          </Flex>
        }
      />

      <Tabs className="custom-tabs" onChange={handleTabChange}>
        {canEdit?.can && (
          <Tabs.TabPane tab="Informação" key="info">
            <TabInfo
              formProps={formProps}
              onFinish={onFinish}
              course={course}
            />
          </Tabs.TabPane>
        )}

        {canSeeDisciplinesTab && course?.with_disciplines && (
          <Tabs.TabPane tab="Disciplinas" key="disciplines">
            <TabDisciplines />
          </Tabs.TabPane>
        )}

        {canEdit?.can && course?.with_disciplines && course.id && (
          <Tabs.TabPane
            tab="Materiais Institucionais"
            key="institutional_materials"
          >
            <TabInstitutionalMaterials parentKind="course" record={course} />
          </Tabs.TabPane>
        )}

        {canShow?.can && (
          <Tabs.TabPane tab="Alunos" key="students">
            <TabStudents />
          </Tabs.TabPane>
        )}

        {canEdit?.can && (
          <Tabs.TabPane tab="Assets" key="assets">
            <TabAssets
              formProps={formProps}
              onFinish={onFinish}
              course={course}
            />
          </Tabs.TabPane>
        )}

        {canEdit?.can && (
          <Tabs.TabPane tab="Funcionalidades" key="features">
            <TabFeatures
              formProps={formProps}
              onFinish={onFinish}
              course={course}
            />
          </Tabs.TabPane>
        )}

        {canSeeAcademicCalendarTab && (
          <Tabs.TabPane tab="Calendário Acadêmico" key="academic_calendar">
            <TabAcademicCalendar
              resourcePrefix="courses"
              canList={Boolean(canListAcademicCalendars?.can)}
              canCreate={Boolean(canCreateAcademicCalendars?.can)}
              canEdit={Boolean(canEditAcademicCalendars?.can)}
              canDelete={Boolean(canDeleteAcademicCalendars?.can)}
            />
          </Tabs.TabPane>
        )}
      </Tabs>
    </EditLayout>
  );
};
